import type { queries } from "@dashboard/contracts";
import { AppRoutes } from "@fero/utils";
import RenewalIcon from "@mui/icons-material/Autorenew";
import CelebrationIcon from "@mui/icons-material/Celebration";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { Box, Paper, Stack, Typography } from "@mui/material";
import type { OrganisationImportantReminders } from "adl-gen/ferovinum/app/api";
import { isLoaded, type LoadingValue } from "utils/utility-types";
import type { z } from "zod";
import { ReminderCard } from "./reminder-card";

/**
 * Todo types imported from @dashboard/contracts
 */
export type Todo = z.infer<typeof queries.todos.Todo>;

interface Props {
  loadingImportantReminders: LoadingValue<OrganisationImportantReminders>;
  todos: {
    data?: Todo[];
    isLoading?: boolean;
    error?: any;
    isSuccess?: boolean;
    isError?: boolean;
  };
}

const getTodoLink = (refType: Todo["refType"], refId: string): string => {
  switch (refType) {
    case "newDealRequest":
      return `${AppRoutes.OrganisationNewDealRequestDetails}/${refId}`;
    case "dealExtensionRequest":
      return `${AppRoutes.OrganisationDealExtensionRequestDetails}/${refId}`;
    default:
      return AppRoutes.Dashboard;
  }
};

const getTodoIcon = (type: Todo["type"]): React.ReactNode => {
  const iconProps = { sx: { color: "warning.main" } };
  console.log(type);

  switch (type) {
    case "newDealContractReview":
      return <PendingActionsIcon {...iconProps} />;
    case "renewalContractReview":
      return <RenewalIcon {...iconProps} />;
    case "procurementDepositPayment":
      return <ReceiptIcon {...iconProps} />;
    case "renewalFeesPayment":
      return <ReceiptIcon {...iconProps} />;
    default:
      return <PendingActionsIcon {...iconProps} />;
  }
};

export const ToDoList = ({ loadingImportantReminders, todos }: Props) => {
  const todoItems = todos.data || [];

  const hasExpiringStock =
    isLoaded(loadingImportantReminders) &&
    loadingImportantReminders.value.expiringStock !== null;
  const hasTodoItems = todoItems.length > 0;
  const isEmpty = !hasTodoItems;

  const ToDoItemCard = ({ item }: { item: Todo }) => (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        display: "flex",
        alignItems: "center",
        gap: 2,
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "grey.50",
        },
      }}
      onClick={() => {
        window.location.href = getTodoLink(item.refType, item.refId);
      }}
    >
      <Box
        sx={{
          backgroundColor: "warning.light",
          p: 1.5,
          borderRadius: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {getTodoIcon(item.type)}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: 500, color: "#333" }}>
          {item.title}
        </Typography>
        <Typography variant="body2" sx={{ color: "#1976d2" }}>
          {item.identifier}
        </Typography>
      </Box>
    </Paper>
  );

  const EmptyState = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        py: 4,
      }}
    >
      <Box
        sx={{
          backgroundColor: "common.lightPurple",
          p: 3,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 3,
        }}
      >
        <CelebrationIcon sx={{ fontSize: 48, color: "common.purple" }} />
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: "#333" }}>
        Whoohoo!
      </Typography>
      <Typography variant="body1" sx={{ color: "#666" }}>
        You have no notifications
      </Typography>
    </Box>
  );

  if (todos.isLoading) {
    return (
      <Paper
        sx={{
          p: 3,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#e8e6f1",
          overflow: "hidden",
        }}
      >
        <Typography variant="body1" sx={{ color: "#666" }}>
          Loading todos...
        </Typography>
      </Paper>
    );
  }

  if (todos.isError) {
    return (
      <Paper
        sx={{
          p: 3,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#e8e6f1",
          overflow: "hidden",
        }}
      >
        <Typography variant="body1" sx={{ color: "#666" }}>
          Failed to load todos
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#e8e6f1",
        overflow: "hidden",
        pt: 3,
        px: 3,
        pb: 0,
        gap: 2,
      }}
    >
      {/* Reminder card */}
      {hasExpiringStock && (
        <Box sx={{ flexShrink: 0 }}>
          <ReminderCard
            title="Review Expiring Deals"
            body="Some deals are expiring soon. Let us know how you would like to manage it."
            buttonProps={{
              label: "REVIEW NOW",
              route: AppRoutes.OrganisationExpiringStock,
            }}
          />
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          width: "100%",
          flexShrink: 0,
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Your To-Do List
        </Typography>
      </Box>

      {/* Scrollable content area for todos only */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          minHeight: 0,
          pb: 3,
          "&::-webkit-scrollbar": {
            display: "none",
          },
          "-ms-overflow-style": "none",
          "scrollbar-width": "none",
        }}
      >
        {isEmpty ? (
          <Box>
            <EmptyState />
          </Box>
        ) : (
          <Stack spacing={2}>
            {/* Only todo items from the hook */}
            {todoItems.map((item) => (
              <ToDoItemCard
                key={`${item.refId}-${item.identifier}`}
                item={item}
              />
            ))}
          </Stack>
        )}
      </Box>
    </Paper>
  );
};
